import { useCallback, useEffect, useRef, useState } from 'react';
import { useFitnessResource } from '@/hooks/useFitnessResource';
import { notifyFitnessChanged } from '@/lib/fitnessChanges';
import { readFitnessPreferences, saveFitnessInstructorPreference } from '@/lib/fitness/fitnessPreferences';
import { DEFAULT_FITNESS_INSTRUCTOR_ID, type FitnessInstructorId } from '@/lib/fitnessInstructor';

export function useFitnessPreferences(ownerId: string | null) {
  const resource = useFitnessResource(readFitnessPreferences);
  const { userId: resourceUserId, status: resourceStatus, data: resourceData, capture, refresh } = resource;
  const latestOwner = useRef(ownerId); latestOwner.current = ownerId;
  const operation = useRef<{ scope: string; ticket: symbol } | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => { operation.current = null; setPending(null); setFailed(null); }, [ownerId]);
  const saveInstructor = useCallback(async (id: FitnessInstructorId) => {
    if (!ownerId || resourceUserId !== ownerId || resourceStatus !== 'ready' || operation.current?.scope === ownerId) return false;
    const ownerCurrent = capture(), ticket = Symbol();
    const current = () => ownerCurrent() && latestOwner.current === ownerId && operation.current?.ticket === ticket;
    operation.current = { scope: ownerId, ticket }; setPending(ownerId); setFailed(null);
    try {
      await saveFitnessInstructorPreference(ownerId, id, () => { if (!current()) throw new Error('Fitness account changed'); });
      if (!current()) return false;
      notifyFitnessChanged();
      return true;
    } catch {
      if (current()) { console.error('[Fitness] instructor preference save failed'); setFailed(ownerId); }
      return false;
    } finally { if (current()) { setPending(null); operation.current = null; } }
  }, [ownerId, resourceUserId, resourceStatus, capture]);
  const matches = !!ownerId && resourceUserId === ownerId;
  const retry = useCallback(() => { setFailed(null); return refresh(); }, [refresh]);
  return {
    instructorId: matches ? resourceData?.instructorId ?? DEFAULT_FITNESS_INSTRUCTOR_ID : DEFAULT_FITNESS_INSTRUCTOR_ID,
    status: matches ? resourceStatus : ownerId ? 'loading' as const : 'signed-out' as const,
    busy: pending === ownerId && !!ownerId,
    error: matches && (resourceStatus === 'error' || failed === ownerId),
    retry, saveInstructor,
  };
}
